import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Employee, TimeLog, EmployeeStatus, LeaveEntry, EditableTimeSegment, PayrollSettings, PayrollFrequency, OvertimeRule, RateDefinition, DayOfWeek, Activity, ActivityCalculation, DisplayGroup, DisplayGroupSettings, GroupInterface, InOutBoardDisplay, InOutBoardSelection, TimeSettings, TimeRounding, LeaveCategory, LeaveCategoryCalculation, EmployeeRole, NameDisplayFormat, Shift, ClockOutRules, TimeEntrySource, Schedules, EmployeeSchedule, CompanySettings, PinpadSize, ScreensaverSettings, TimestampSource, SMTPSettings, NotificationSettings, WeatherUnits } from '../../shared/types';
import { LOCAL_STORAGE_KEYS } from '../../shared/constants';
import { processTimesheets, toDateInputString, cloneDeep, calculateDaysBetween, getTodaysShiftForEmployee, calculatePeriodDates, calculatePreviousPeriodStartDate, correctDateForTimezone } from '../utils.js';

const CORE_ACTIVITIES: Activity[] = [
    { id: 'core_in', name: 'In', calculation: ActivityCalculation.PAID },
    { id: 'core_lunch', name: 'Lunch', calculation: ActivityCalculation.UNPAID },
];

const DEFAULT_SHIFT_ID = 'default-shift-id';

const defaultDisplayGroupSettings: DisplayGroupSettings = {
    groupInterface: GroupInterface.GRID,
    options: {
        requireEmployeePINs: true,
    },
    nameFormat: NameDisplayFormat.FIRST_LAST,
    shiftId: DEFAULT_SHIFT_ID,
    breaksAndLunches: {
        showBreakButton: false,
        showLunchButton: true,
    },
    messaging: {
        enableMailForEmployees: false,
        enableMailForManagers: false,
    },
    inOutBoardOptions: {
        display: InOutBoardDisplay.ONE_LIST,
        columns: {
            status: true,
            date: true,
            time: true,
            elapsed: true,
            messages: false,
        },
        selection: InOutBoardSelection.EMPLOYEE_DASHBOARD,
    },
};

const defaultScreensaverSettings: ScreensaverSettings = {
    enabled: false,
    timeoutMinutes: 15,
    widgets: {
        time: true,
        date: true,
        weather: false,
    },
    latitude: 52.52,
    longitude: 13.41,
    weatherUnits: WeatherUnits.IMPERIAL,
};

const defaultNotificationSettings: NotificationSettings = {
    notifyOnMissedPunch: false,
    ccManagers: true,
    adminIdsToNotify: [],
};

interface ImportResult {
  added: number;
  updated: number;
  processedNames?: string[];
  employeeIds?: string[];
}

interface TimeClockContextType {
  employees: Employee[];
  timeLogs: TimeLog[];
  leaveEntries: LeaveEntry[];
  payrollSettings: PayrollSettings | null;
  overtimeRules: OvertimeRule[];
  activities: Activity[];
  displayGroups: DisplayGroup[];
  timeSettings: TimeSettings | null;
  screensaverSettings: ScreensaverSettings;
  leaveCategories: LeaveCategory[];
  shifts: Shift[];
  schedules: Schedules;
  companySettings: CompanySettings | null;
  smtpSettings: SMTPSettings | null;
  notificationSettings: NotificationSettings | null;
  isLoading: boolean;
  pinpadSize: PinpadSize;
  groupContentSizes: {[groupId: string]: number};
  addEmployee: (data: { emp_number: number, pin: string }) => Promise<void>;
  updateEmployee: (employee: Employee) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;
  findEmployeeByPin: (pin: string) => Employee | undefined;
  handleClockAction: (employeeId: string, timestamp?: number, source?: TimeEntrySource) => Promise<EmployeeStatus>;
  handleBreakAction: (employeeId: string, timestamp?: number) => Promise<EmployeeStatus>;
  getProcessedTimesheets: (startDate: Date, endDate: Date) => ReturnType<typeof processTimesheets>;
  updateTimeLog: (id: string, updates: Partial<Omit<TimeLog, 'id'>>) => Promise<void>;
  deleteTimeLogs: (logIds: string[]) => Promise<void>;
  addTimeLog: (log: Omit<TimeLog, 'id' | 'createdAt' | 'employeeId'> & { employeeId: number }) => Promise<TimeLog>;
  addLeaveEntry: (data: Omit<LeaveEntry, 'id' | 'createdAt'>) => Promise<void>;
  updateLeaveEntry: (id: string, updates: Partial<LeaveEntry>) => Promise<void>;
  deleteLeaveEntry: (id: string) => Promise<void>;
  insertLunchBreak: (workLogId: string, lunchStart: number, lunchEnd: number, workLogObject?: TimeLog) => Promise<void>;
  updatePayrollSettings: (settings: PayrollSettings) => Promise<void>;
  closePeriod: () => Promise<void>;
  reopenPeriod: () => Promise<void>;
  addOvertimeRule: (rule: Omit<OvertimeRule, 'id' | 'assignedEmployeeIds'>) => Promise<void>;
  updateOvertimeRule: (rule: OvertimeRule) => Promise<void>;
  deleteOvertimeRule: (ruleId: string) => Promise<void>;
  assignOvertimeRuleToEmployee: (employeeId: string, newRuleId: string) => Promise<void>;
  setOvertimeRuleAssignments: (ruleId: string, newEmployeeIds: string[]) => Promise<void>;
  addDisplayGroup: (name: string) => Promise<void>;
  updateDisplayGroup: (group: DisplayGroup) => Promise<void>;
  deleteDisplayGroup: (id: string) => Promise<void>;
  getGroupForEmployee: (employeeId: string) => DisplayGroup | undefined;
  updateTimeSettings: (settings: TimeSettings) => Promise<void>;
  updatePinpadSize: (size: PinpadSize) => void;
  updateGroupContentSize: (groupId: string, size: number) => void;
  updateScreensaverSettings: (settings: ScreensaverSettings) => Promise<void>;
  updateCompanySettings: (settings: CompanySettings) => Promise<void>;
  updateSmtpSettings: (settings: SMTPSettings) => Promise<void>;
  updateNotificationSettings: (settings: NotificationSettings) => Promise<void>;
  importEmployees: (data: Partial<Employee>[], update: boolean) => Promise<ImportResult>;
  importTimeLogs: (data: any[], update: boolean) => Promise<ImportResult>;
  importCustomLeave: (data: any[], update: boolean) => Promise<ImportResult>;
  addShift: (name: string) => Promise<void>;
  updateShift: (shift: Shift) => Promise<void>;
  deleteShift: (id: string) => Promise<void>;
  getActivityId: (name: 'In' | 'Lunch') => string | undefined;
  updateEmployeeSchedule: (employeeId: string, schedule: EmployeeSchedule) => Promise<void>;
  completeSetup: (data: any) => Promise<void>;
  updateLeaveClassifications: (classifications: Pick<LeaveCategory, 'id' | 'calculation'>[]) => Promise<void>;
  addCustomLeaveCategory: (name: string, calculation: LeaveCategoryCalculation) => Promise<void>;
  updateCustomLeaveCategory: (category: LeaveCategory) => Promise<void>;
  deleteCustomLeaveCategory: (id: string) => Promise<void>;
  fetchData: () => Promise<void>;
}

const TimeClockContext = createContext<TimeClockContextType | null>(null);

const getInitialScreensaverSettings = (): ScreensaverSettings => {
    try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.SCREENSAVER_SETTINGS);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                ...defaultScreensaverSettings,
                ...parsed,
                widgets: {
                    ...defaultScreensaverSettings.widgets,
                    ...(parsed.widgets || {})
                }
            };
        }
    } catch (error) {
        console.error("Error reading screensaver settings from localStorage", error);
    }
    return defaultScreensaverSettings;
};

export const TimeClockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [leaveEntries, setLeaveEntries] = useState<LeaveEntry[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [schedules, setSchedules] = useState<Schedules>({});
  const [payrollSettings, setPayrollSettings] = useState<PayrollSettings | null>(null);
  const [overtimeRules, setOvertimeRules] = useState<OvertimeRule[]>([]);
  const [activities] = useState<Activity[]>(CORE_ACTIVITIES);
  const [displayGroups, setDisplayGroups] = useState<DisplayGroup[]>([]);
  const [timeSettings, setTimeSettings] = useState<TimeSettings | null>(null);
  const [screensaverSettings, setScreensaverSettings] = useState<ScreensaverSettings>(getInitialScreensaverSettings());
  const [leaveCategories, setLeaveCategories] = useState<LeaveCategory[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [smtpSettings, setSmtpSettings] = useState<SMTPSettings | null>(null);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null);
  const [pinpadSize, setPinpadSize] = useState<PinpadSize>(() => {
    const storedSize = localStorage.getItem(LOCAL_STORAGE_KEYS.PINPAD_SIZE) as PinpadSize | null;
    return storedSize || PinpadSize.MEDIUM;
  });
  const [groupContentSizes, setGroupContentSizes] = useState<{[groupId: string]: number}>(() => {
      try {
          const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.GROUP_CONTENT_SIZES);
          return stored ? JSON.parse(stored) : {};
      } catch (e) {
          console.error("Failed to parse group content sizes from localStorage", e);
          return {};
      }
  });
  const fetchData = useCallback(async () => {
      try {
          const response = await fetch('/api/data');
          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to fetch initial data.');
          }
          const data = await response.json();
          setEmployees(data.employees || []);
          setTimeLogs(data.timeLogs || []);
          setLeaveEntries(data.leaveEntries || []);
          setShifts(data.shifts || []);
          setSchedules(data.schedules || {});
          setPayrollSettings(data[LOCAL_STORAGE_KEYS.PAYROLL_SETTINGS] || null);
          setOvertimeRules(data.overtimeRules || []);
          const rawDisplayGroups: DisplayGroup[] = data.displayGroups || [];
          const mergedDisplayGroups = rawDisplayGroups.map((group) => {
              const settings: Partial<DisplayGroupSettings> = group.settings || {};
              const mergedSettings: DisplayGroupSettings = {
                  ...defaultDisplayGroupSettings,
                  ...settings,
                  options: {
                      ...defaultDisplayGroupSettings.options,
                      ...(settings.options || {}),
                  },
                  breaksAndLunches: {
                      ...defaultDisplayGroupSettings.breaksAndLunches,
                      ...(settings.breaksAndLunches || {}),
                  },
                  messaging: {
                      ...defaultDisplayGroupSettings.messaging,
                      ...(settings.messaging || {}),
                  },
                  inOutBoardOptions: {
                      ...defaultDisplayGroupSettings.inOutBoardOptions,
                      ...(settings.inOutBoardOptions || {}),
                      columns: {
                          ...defaultDisplayGroupSettings.inOutBoardOptions.columns,
                          ...(settings.inOutBoardOptions?.columns || {}),
                      },
                  },
              };
              return { ...group, settings: mergedSettings };
          });
          setDisplayGroups(mergedDisplayGroups);
          setTimeSettings(data[LOCAL_STORAGE_KEYS.TIME_SETTINGS] || null);
          const rawNotificationSettings = data[LOCAL_STORAGE_KEYS.NOTIFICATION_SETTINGS] || {};
          setNotificationSettings({
              ...defaultNotificationSettings,
              ...rawNotificationSettings,
          });
          setLeaveCategories(data.leaveCategories || []);
          setCompanySettings(data[LOCAL_STORAGE_KEYS.COMPANY_SETTINGS] || null);
          setSmtpSettings(data[LOCAL_STORAGE_KEYS.SMTP_SETTINGS] || null);
          setError(null);
      } catch (err: any) {
          setError(err.message);
          // no-op
      } finally {
          setIsLoading(false);
      }
  }, []);
  useEffect(() => {
    fetchData();
    const intervalId = setInterval(() => {
      const onAdminPage = location.pathname.startsWith('/admin');
      if (!onAdminPage) {
        fetchData();
      }
    }, 10000);
    return () => clearInterval(intervalId);
  }, [fetchData, location.pathname]);
  const apiCall = async (endpoint: string, method: string, body?: any) => {
    const options: RequestInit = {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    };
    const response = await fetch(endpoint, options);
    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `${method} request to ${endpoint} failed`);
    }
    return response.status !== 204 ? await response.json() : null;
  };
  const completeSetup = useCallback(async (wizardData: any) => {
    await apiCall('/api/setup/complete', 'POST', wizardData);
    await fetchData();
  }, [fetchData]);
  const addEmployee = useCallback(async (data: { emp_number: number, pin: string }) => {
    await apiCall('/api/employees', 'POST', data);
    await fetchData();
  }, [fetchData]);
  const updateEmployee = useCallback(async (employee: Employee) => {
    await apiCall(`/api/employees/${employee.id}`, 'PUT', employee);
    await fetchData();
  }, [fetchData]);
  const deleteEmployee = useCallback(async (id: string) => {
    await apiCall(`/api/employees/${id}`, 'DELETE');
    await fetchData();
  }, [fetchData]);
  const findEmployeeByPin = useCallback((pin: string) => employees.find(e => e.pin === pin), [employees]);
  const isClosedPayrollTimestamp = useCallback((timestamp: number) => {
    if (!payrollSettings?.startDate) return false;
    const lockDate = correctDateForTimezone(payrollSettings.startDate);
    lockDate.setHours(0, 0, 0, 0);
    return timestamp < lockDate.getTime();
  }, [payrollSettings?.startDate]);
  const handleClockAction = useCallback(async (employeeId: string, timestamp?: number, source: TimeEntrySource = TimeEntrySource.MANUAL): Promise<EmployeeStatus> => {
    const effectiveTimestamp = typeof timestamp === 'number' ? timestamp : Date.now();
    if (isClosedPayrollTimestamp(effectiveTimestamp)) {
      throw new Error('Cannot punch in or out for a closed pay period.');
    }
    const result = await apiCall(`/api/employees/${employeeId}/clock`, 'POST', { timestamp, source });
    await fetchData();
    return result.newStatus;
  }, [fetchData, isClosedPayrollTimestamp]);
  const handleBreakAction = useCallback(async (employeeId: string, timestamp?: number): Promise<EmployeeStatus> => {
    const result = await apiCall(`/api/employees/${employeeId}/break`, 'POST', { timestamp });
    await fetchData();
    return result.newStatus;
  }, [fetchData]);
  const getProcessedTimesheets = useCallback(() => [], []);
  const addTimeLog = useCallback(async (log: Omit<TimeLog, 'id' | 'createdAt' | 'employeeId'> & { employeeId: number }): Promise<TimeLog> => {
    const newLog = await apiCall('/api/time-logs', 'POST', log);
    await fetchData();
    return newLog;
  }, [fetchData]);
  const addLeaveEntry = useCallback(async (data: Omit<LeaveEntry, 'id' | 'createdAt'>) => {
    await apiCall('/api/leave-entries', 'POST', data);
    await fetchData();
  }, [fetchData]);
  const updateLeaveEntry = useCallback(async (id: string, updates: Partial<LeaveEntry>) => {
    await apiCall(`/api/leave-entries/${id}`, 'PUT', updates);
    await fetchData();
  }, [fetchData]);
  const deleteLeaveEntry = useCallback(async (id: string) => {
    await apiCall(`/api/leave-entries/${id}`, 'DELETE');
    await fetchData();
  }, [fetchData]);
  const updateTimeLog = useCallback(async (id: string, updates: Partial<Omit<TimeLog, 'id'>>) => {
    await apiCall(`/api/time-logs/${id}`, 'PUT', updates);
    await fetchData();
  }, [fetchData]);
  const deleteTimeLogs = useCallback(async (logIds: string[]) => {
    await apiCall('/api/time-logs/delete-multiple', 'POST', { logIds });
    await fetchData();
  }, [fetchData]);
  const insertLunchBreak = useCallback(async (workLogId: string, lunchStart: number, lunchEnd: number) => {
    await apiCall(`/api/time-logs/${workLogId}/insert-lunch`, 'POST', { lunchStart, lunchEnd });
    await fetchData();
  }, [fetchData]);
  const updateSettings = useCallback(async (key: string, settings: any) => {
    await apiCall(`/api/settings/${key}`, 'PUT', settings);
    await fetchData();
  }, [fetchData]);
  const updatePayrollSettings = useCallback((settings: PayrollSettings) => updateSettings(LOCAL_STORAGE_KEYS.PAYROLL_SETTINGS, settings), [updateSettings]);
  const updateTimeSettings = useCallback((settings: TimeSettings) => updateSettings(LOCAL_STORAGE_KEYS.TIME_SETTINGS, settings), [updateSettings]);
  const updatePinpadSize = useCallback((size: PinpadSize) => {
    setPinpadSize(size);
    localStorage.setItem(LOCAL_STORAGE_KEYS.PINPAD_SIZE, size);
  }, []);
  const updateGroupContentSize = useCallback((groupId: string, size: number) => {
      setGroupContentSizes(prev => {
          const newSizes = { ...prev, [groupId]: size };
          localStorage.setItem(LOCAL_STORAGE_KEYS.GROUP_CONTENT_SIZES, JSON.stringify(newSizes));
          return newSizes;
      });
  }, []);
  const updateScreensaverSettings = useCallback(async (settings: ScreensaverSettings) => {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEYS.SCREENSAVER_SETTINGS, JSON.stringify(settings));
        setScreensaverSettings(settings);
    } catch (error) {
        console.error("Failed to save screensaver settings to localStorage", error);
    }
  }, []);
  const updateCompanySettings = useCallback((settings: CompanySettings) => updateSettings(LOCAL_STORAGE_KEYS.COMPANY_SETTINGS, settings), [updateSettings]);
  const updateSmtpSettings = useCallback((settings: SMTPSettings) => updateSettings(LOCAL_STORAGE_KEYS.SMTP_SETTINGS, settings), [updateSettings]);
  const updateNotificationSettings = useCallback((settings: NotificationSettings) => updateSettings(LOCAL_STORAGE_KEYS.NOTIFICATION_SETTINGS, settings), [updateSettings]);
  const closePeriod = useCallback(async () => {
    if (!payrollSettings) return;
    const { end: currentPeriodEnd } = calculatePeriodDates(payrollSettings);
    const nextPeriodStart = new Date(currentPeriodEnd.getTime());
    nextPeriodStart.setDate(currentPeriodEnd.getDate() + 1);
    const newSettings = { ...payrollSettings, startDate: toDateInputString(nextPeriodStart), endDate: undefined };
    await updatePayrollSettings(newSettings);
  }, [payrollSettings, updatePayrollSettings]);
  const reopenPeriod = useCallback(async () => {
      if (!payrollSettings) return;
      const prevStartDate = calculatePreviousPeriodStartDate(payrollSettings);
      const newSettings = { ...payrollSettings, startDate: toDateInputString(prevStartDate), endDate: undefined };
      await updatePayrollSettings(newSettings);
  }, [payrollSettings, updatePayrollSettings]);
  const addOvertimeRule = useCallback(async (ruleData: Omit<OvertimeRule, 'id' | 'assignedEmployeeIds'>) => {
    await apiCall('/api/overtime-rules', 'POST', ruleData);
    await fetchData();
  }, [fetchData]);
  const updateOvertimeRule = useCallback(async (rule: OvertimeRule) => {
    await apiCall(`/api/overtime-rules/${rule.id}`, 'PUT', rule);
    await fetchData();
  }, [fetchData]);
  const deleteOvertimeRule = useCallback(async (ruleId: string) => {
    await apiCall(`/api/overtime-rules/${ruleId}`, 'DELETE');
    await fetchData();
  }, [fetchData]);
  const setOvertimeRuleAssignments = useCallback(async (ruleId: string, employeeIds: string[]) => {
    await apiCall(`/api/overtime-rules/${ruleId}/assignments`, 'PUT', { employeeIds });
    await fetchData();
  }, [fetchData]);
  const assignOvertimeRuleToEmployee = useCallback(async (employeeId: string, newRuleId: string) => {
    await apiCall(`/api/employees/${employeeId}/overtime-rule`, 'PUT', { ruleId: newRuleId });
    await fetchData();
  }, [fetchData]);
  const addDisplayGroup = useCallback(async (name: string) => {
    const newGroupData = { name, employeeIds: [], managerIds: [], settings: defaultDisplayGroupSettings };
    await apiCall('/api/display-groups', 'POST', newGroupData);
    await fetchData();
  }, [fetchData]);
  const updateDisplayGroup = useCallback(async (group: DisplayGroup) => {
    await apiCall(`/api/display-groups/${group.id}`, 'PUT', group);
    await fetchData();
  }, [fetchData]);
  const deleteDisplayGroup = useCallback(async (id: string) => {
    await apiCall(`/api/display-groups/${id}`, 'DELETE');
    await fetchData();
  }, [fetchData]);
  const getGroupForEmployee = useCallback((employeeId: string) => {
    return displayGroups.find(g => g.employeeIds.includes(employeeId) && !g.isDefault) || displayGroups.find(g => g.isDefault);
  }, [displayGroups]);
  const importEmployees = useCallback(async (data: Partial<Employee>[], update: boolean): Promise<ImportResult> => {
    try {
        const result = await apiCall('/api/import/employees', 'POST', { data, update });
        await fetchData();
        return result;
    } catch (error) {
        console.error("Employee import failed:", error);
        throw error;
    }
  }, [fetchData]);
  const importTimeLogs = useCallback(async (data: any[], update: boolean): Promise<ImportResult> => {
    const processedData = data.map(log => {
      if (!log.startDate || !log.startTime) return log;
      try {
        const startTimeObj = new Date(`${log.startDate}T${log.startTime}`);
        if (isNaN(startTimeObj.getTime())) throw new Error('Invalid start date/time');
        const startTime = startTimeObj.getTime();
        let endTime: number | null = null;
        if (log.endTime) {
            const endDateStr = log.endDate || log.startDate;
            const endTimeObj = new Date(`${endDateStr}T${log.endTime}`);
            if (isNaN(endTimeObj.getTime())) throw new Error('Invalid end date/time');
            if (endTimeObj < startTimeObj) {
                endTimeObj.setDate(endTimeObj.getDate() + 1);
            }
            endTime = endTimeObj.getTime();
        }
        const { startDate, startTime: st, endDate, endTime: et, ...rest } = log;
        return { ...rest, startTime, endTime };
      } catch (e) {
          console.warn('Skipping row due to invalid date/time format:', log, e);
          return { ...log, invalid: true };
      }
    });
    try {
        const result = await apiCall('/api/import/timelogs', 'POST', { data: processedData, update });
        await fetchData();
        return result;
    } catch (error) {
        console.error("Time log import failed:", error);
        throw error;
    }
  }, [fetchData]);
  const importCustomLeave = useCallback(async (data: any[], update: boolean): Promise<ImportResult> => {
      try {
          const result = await apiCall('/api/import/custom-leave', 'POST', { data, update });
          await fetchData();
          return result;
      } catch (error) {
          console.error("Custom leave import failed:", error);
          throw error;
      }
  }, [fetchData]);
  const addShift = useCallback(async (name: string) => {
    const newShiftData = { name, startTime: '09:00', stopTime: '17:00', lunchMinutes: 60 };
    await apiCall('/api/shifts', 'POST', newShiftData);
    await fetchData();
  }, [fetchData]);
  const updateShift = useCallback(async (shift: Shift) => {
    await apiCall(`/api/shifts/${shift.id}`, 'PUT', shift);
    await fetchData();
  }, [fetchData]);
  const deleteShift = useCallback(async (id: string) => {
    await apiCall(`/api/shifts/${id}`, 'DELETE');
    await fetchData();
  }, [fetchData]);
  const getActivityId = useCallback((name: 'In' | 'Lunch') => activities.find(a => a.name === name)?.id, [activities]);
  const updateEmployeeSchedule = useCallback(async (employeeId: string, schedule: EmployeeSchedule) => {
    await apiCall(`/api/schedules/${employeeId}`, 'PUT', schedule);
    await fetchData();
  }, [fetchData]);
  const updateLeaveClassifications = useCallback(async (classifications: Pick<LeaveCategory, 'id' | 'calculation'>[]) => {
    const payload = classifications.map(c => ({
        leave_type_id: c.id,
        calculation: c.calculation,
    }));
    await apiCall('/api/leave-classifications', 'PUT', payload);
    await fetchData();
  }, [fetchData]);
  const addCustomLeaveCategory = useCallback(async (name: string, calculation: LeaveCategoryCalculation) => {
    await apiCall('/api/custom-leave-categories', 'POST', { name, calculation });
    await fetchData();
  }, [fetchData]);
  const updateCustomLeaveCategory = useCallback(async (category: LeaveCategory) => {
    await apiCall(`/api/custom-leave-categories/${category.id}`, 'PUT', category);
    await fetchData();
  }, [fetchData]);
  const deleteCustomLeaveCategory = useCallback(async (id: string) => {
    await apiCall(`/api/custom-leave-categories/${id}`, 'DELETE');
    await fetchData();
  }, [fetchData]);
  const value = {
    employees, timeLogs, leaveEntries, payrollSettings, overtimeRules, activities, displayGroups, timeSettings, screensaverSettings, leaveCategories, shifts, schedules, companySettings, smtpSettings, notificationSettings,
    isLoading, pinpadSize, groupContentSizes,
    addEmployee, updateEmployee, deleteEmployee, findEmployeeByPin,
    handleClockAction, handleBreakAction, getProcessedTimesheets,
    updateTimeLog, deleteTimeLogs, addTimeLog, addLeaveEntry, updateLeaveEntry, deleteLeaveEntry,
    insertLunchBreak, updatePayrollSettings, closePeriod, reopenPeriod,
    addOvertimeRule, updateOvertimeRule, deleteOvertimeRule, assignOvertimeRuleToEmployee, setOvertimeRuleAssignments,
    addDisplayGroup, updateDisplayGroup, deleteDisplayGroup, getGroupForEmployee,
    updateTimeSettings, updatePinpadSize, updateGroupContentSize, updateScreensaverSettings, updateCompanySettings, updateSmtpSettings, updateNotificationSettings,
    importEmployees, importTimeLogs, importCustomLeave,
    addShift, updateShift, deleteShift,
    getActivityId, updateEmployeeSchedule,
    completeSetup,
    updateLeaveClassifications,
    addCustomLeaveCategory, updateCustomLeaveCategory, deleteCustomLeaveCategory,
    fetchData,
  };
  return <TimeClockContext.Provider value={value}>{children}</TimeClockContext.Provider>;
};

export const useTimeClock = () => {
  const context = useContext(TimeClockContext);
  if (!context) {
    throw new Error('useTimeClock must be used within a TimeClockProvider');
  }
  return context;
};
